import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeSelection";
import { useUserSettings } from "../contexts/UserSettingsContext";
import NotificationBell from "./NotificationBell";
import WhatsNewModal, { WhatsNewButton, useWhatsNew } from "./WhatsNewModal";
import useFocusMode from "../hooks/useFocusMode";
import { useTopBarActions } from "../contexts/TopBarActionsContext";

import styles from "../styles/SecondaryTopBar.module.css";

interface BackRoute {
  label: string;
  to: string;
}

const getBackRoute = (pathname: string): BackRoute | null => {
  if (pathname.startsWith("/notes/note/")) return { label: "Notes", to: "/notes/" };
  if (pathname.startsWith("/lists/list/")) return { label: "Lists", to: "/lists/" };
  if (pathname === "/health/insights") return { label: "Health", to: "/health" };
  if (pathname === "/admin") return { label: "Account", to: "/account" };
  return null;
};

const HERO_SCROLL_THRESHOLD = 200;

const SecondaryTopBar: React.FC = () => {
  const { activeTheme, theme } = useTheme();
  const location = useLocation();
  const backRoute = getBackRoute(location.pathname);
  const isHome = location.pathname === "/";
  const [scrolledPastHero, setScrolledPastHero] = useState(false);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const { entries, hasUnseen, lastSeenTimestamp } = useWhatsNew();
  const { focusModeEnabled, profile, primaryUseCase } = useFocusMode();
  const navigate = useNavigate();
  const { action: pageAction } = useTopBarActions();

  const { settings } = useUserSettings();
  const profilePhoto = settings.profilePhoto || "";

  // Focus mode on home — top bar floats over the hero gradient (transparent)
  const isFocusHome = isHome && focusModeEnabled;
  // Home page has a hero — top bar starts transparent, goes solid on scroll
  const hasHero = isHome; // both normal home (cycle hero) and focus home (running hero)

  // Derive a contextual "calendar" shortcut link from the focus profile
  const focusCalendarLink = useMemo(() => {
    if (!focusModeEnabled || !profile) return null;
    // Prioritise the actual calendar tab, then fall back to plan/cycle
    const calendarTab = profile.tabNav.find((t) => t.id.includes("calendar"));
    if (calendarTab) return calendarTab.path;
    const altTab = profile.tabNav.find((t) => t.id.includes("plan"));
    return altTab?.path ?? "/calendar/";
  }, [focusModeEnabled, profile]);

  // Derive current page title from focus profile tabNav based on route
  const focusPageTitle = useMemo(() => {
    if (!focusModeEnabled || !profile) return "";
    const pathname = location.pathname;
    const search = location.search;
    const fullPath = pathname + search;
    // Try exact match first (path + query)
    for (const tab of profile.tabNav) {
      if (tab.path === fullPath) return tab.label;
    }
    // Try pathname-only match (for pages with different query params)
    for (const tab of profile.tabNav) {
      const tabPathname = tab.path.split("?")[0];
      if (tabPathname === pathname && tabPathname !== "/") return tab.label;
    }
    // Fallback for sub-pages (e.g., /account, /admin)
    if (pathname === "/account" || pathname === "/admin") return "Account";
    return "";
  }, [focusModeEnabled, profile, location.pathname, location.search]);

  useEffect(() => {
    if (!hasHero) return;
    const handleScroll = () => setScrolledPastHero(window.scrollY > HERO_SCROLL_THRESHOLD);
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasHero]);

  // The bar uses heroGradient on all solid states (non-home pages + home scrolled past hero)
  const overHero = hasHero && !scrolledPastHero;
  const isSolid = !hasHero || scrolledPastHero;
  // Bar always has gradient or floats over hero — icons are always white
  const iconColor = "#fff";

  return (
    <div
      className={`${styles.topBar} ${isSolid ? styles.topBarSolid : ""}`}
      data-bar="secondary-top"
      style={{
        background: overHero ? "transparent" : theme[activeTheme].heroGradient,
      }}
    >
      <div>
        {backRoute && !focusModeEnabled ? (
          <Link
            to={backRoute.to}
            className={styles.backButton}
            style={{ color: "#fff" }}
          >
            <i className="fa-solid fa-arrow-left" />
            {backRoute.label}
          </Link>
        ) : (
          <Link
            to="/account"
            className={styles.focusAccountIcon}
            style={{ color: iconColor, backgroundColor: profilePhoto ? "transparent" : "rgba(255,255,255,0.2)" }}
          >
            {profilePhoto ? (
              <img src={profilePhoto} alt="" className={styles.accountPhoto} />
            ) : (
              <i className="fa-solid fa-user" />
            )}
          </Link>
        )}
      </div>
      {focusModeEnabled && !isHome && focusPageTitle && (
        <span className={styles.focusPageTitle} style={{ color: "#fff" }}>
          {focusPageTitle}
        </span>
      )}
      <div className={styles.rightSection}>
        {isFocusHome && focusCalendarLink && (
          primaryUseCase === "health" ? (
            <button
              className={styles.focusCalendarIcon}
              style={{ color: iconColor, backgroundColor: "rgba(255,255,255,0.2)", border: "none", cursor: "pointer" }}
              onClick={() => navigate("/womens-health?tab=cycle", { state: { openCalendar: true } })}
              aria-label="Open cycle calendar"
            >
              <i className="fa-solid fa-calendar-days" />
            </button>
          ) : (
            <Link
              to={focusCalendarLink}
              className={styles.focusCalendarIcon}
              style={{ color: iconColor, backgroundColor: "rgba(255,255,255,0.2)" }}
            >
              <i className="fa-solid fa-calendar-days" />
            </Link>
          )
        )}
        {focusModeEnabled && location.pathname === "/womens-health" && (
          <button
            className={styles.focusCalendarIcon}
            style={{ color: iconColor, backgroundColor: "rgba(255,255,255,0.2)", border: "none", cursor: "pointer" }}
            onClick={() => window.dispatchEvent(new CustomEvent("onyxlife:open-cycle-calendar"))}
            aria-label="Open cycle calendar"
          >
            <i className="fa-solid fa-calendar-days" />
          </button>
        )}
        {pageAction}
        <WhatsNewButton onClick={() => setShowWhatsNew(true)} hasUnseen={hasUnseen} />
        <NotificationBell colorOverride="#fff" />
      </div>
      <WhatsNewModal
        show={showWhatsNew}
        onClose={() => setShowWhatsNew(false)}
        entries={entries}
        lastSeenTimestamp={lastSeenTimestamp}
      />
    </div>
  );
};

export default SecondaryTopBar;
